import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

// material-ui
// --- THIS IS THE FIX ---
// We update `useTheme` to import from the modern styling engine.
import { useTheme } from '@mui/material/styles';
import {
    Avatar,
    Box, // Import Box for styling wrappers
    CardContent,
    Chip,
    ClickAwayListener,
    Divider,
    Grid,
    List,
    ListItemIcon,
    ListItemText,
    Paper,
    Popper,
    Typography
} from '@mui/material';
import ListItemButton from '@mui/material/ListItemButton';

// project imports
import MainCard from 'components/cards/MainCard';
import Transitions from 'components/extended/Transitions';
import { selectCustomization } from 'store/customization/customizationSlice';
import { selectUser } from 'store/user/userSlice';

// hooks
import useAuth from 'hooks/useAuth';

// assets
import { IconLogout, IconSettings, IconUserPlus } from '@tabler/icons';

// The legacy `profileStyles.js` import is no longer needed.
// import { useStyles } from './profileStyles';

const ProfileSection = () => {
    // We no longer need the legacy `classes` object.
    // const classes = useStyles();
    const theme = useTheme(); // This hook now correctly receives the global theme.
    const { doLogout } = useAuth();

    const customization = useSelector(selectCustomization);
    const user = useSelector(selectUser);

    const [selectedIndex] = useState(1);
    const [open, setOpen] = useState(false);
    const prevOpen = useRef(open);
    const anchorRef = useRef(null);

    const handleToggle = () => setOpen(prevOpen => !prevOpen);

    const handleClose = evt => {
        if (anchorRef.current && anchorRef.current.contains(evt.target)) {
            return;
        }
        setOpen(false);
    };

    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }
        prevOpen.current = open;
    }, [open]);

    return (
        <>
            <Chip
                // The styles from the old `profileChip` and `profileLabel` classes are now here.
                sx={{
                    height: '48px',
                    alignItems: 'center',
                    borderRadius: '27px',
                    transition: 'all .2s ease-in-out',
                    borderColor: theme.palette.primary.light,
                    backgroundColor: theme.palette.primary.light,
                    '&[aria-controls="menu-list-grow"], &:hover': {
                        borderColor: theme.palette.primary.main,
                        background: `${theme.palette.primary.main}!important`,
                        color: theme.palette.primary.light,
                        '& svg': {
                            stroke: theme.palette.primary.light
                        }
                    },
                    '& .MuiChip-label': {
                        lineHeight: 0
                    }
                }}
                icon={
                    <Avatar
                        src={user?.info?.photoURL || ''}
                        // The styles from the old `headerAvatar` class are now here.
                        sx={{
                            ...theme.typography.mediumAvatar,
                            margin: '8px 0 8px 8px !important',
                            cursor: 'pointer'
                        }}
                        ref={anchorRef}
                        aria-controls={open ? 'menu-list-grow' : undefined}
                        aria-haspopup='true'
                        color='inherit'
                    />
                }
                label={<IconSettings stroke={1.5} size='1.5rem' color={theme.palette.primary.main} />}
                variant='outlined'
                ref={anchorRef}
                aria-controls={open ? 'menu-list-grow' : undefined}
                aria-haspopup='true'
                onClick={handleToggle}
                color='primary'
            />
            <Popper
                placement='bottom-end'
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 14]
                            }
                        }
                    ]
                }}>
                {({ TransitionProps }) => (
                    <Transitions in={open} {...TransitionProps}>
                        <Paper>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MainCard
                                    border={false}
                                    elevation={16}
                                    content={false}
                                    boxShadow
                                    shadow={theme.shadows[16]}>
                                    <CardContent sx={{ p: 2 }}>
                                        <Grid container direction='column' spacing={0}>
                                            <Grid item sx={{ display: 'flex' }}>
                                                <Typography variant='h4'>Hi,</Typography>
                                                <Typography component='span' variant='h4' sx={{ fontWeight: 400, ml: 1 }}>
                                                    {user?.info?.displayName || ''}
                                                </Typography>
                                            </Grid>
                                            <Grid item>
                                                <Typography variant='subtitle2'>Project Admin</Typography>
                                            </Grid>
                                        </Grid>
                                        <Divider sx={{ my: 1 }} />
                                        <List component='nav' sx={{
                                            width: '100%',
                                            maxWidth: 350,
                                            minWidth: 300,
                                            backgroundColor: theme.palette.background.paper,
                                            borderRadius: '10px'
                                        }}>
                                            <ListItemButton
                                                sx={{ borderRadius: `${customization.borderRadius}px` }}
                                                selected={selectedIndex === 4}
                                                onClick={handleClose}
                                                component={Link}
                                                to='/profile'>
                                                <ListItemIcon>
                                                    <IconUserPlus stroke={1.5} size='1.3rem' />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={<Typography variant='body2'>Profile</Typography>}
                                                />
                                            </ListItemButton>
                                            <ListItemButton
                                                sx={{ borderRadius: `${customization.borderRadius}px` }}
                                                selected={selectedIndex === 4}
                                                onClick={handleClose}
                                                component={Link}
                                                to='/preferences'>
                                                <ListItemIcon>
                                                    <IconSettings stroke={1.5} size='1.3rem' />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={<Typography variant='body2'>Preferences</Typography>}
                                                />
                                            </ListItemButton>
                                            <ListItemButton
                                                sx={{ borderRadius: `${customization.borderRadius}px` }}
                                                selected={selectedIndex === 4}
                                                onClick={doLogout}>
                                                <ListItemIcon>
                                                    <IconLogout stroke={1.5} size='1.3rem' />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={<Typography variant='body2'>Logout</Typography>}
                                                />
                                            </ListItemButton>
                                        </List>
                                    </CardContent>
                                </MainCard>
                            </ClickAwayListener>
                        </Paper>
                    </Transitions>
                )}
            </Popper>
        </>
    );
};

export default ProfileSection;